import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import FractionInput from "../../../../components/inputs/FractionInput";

/* =============================================================
   TASK: ComputeFunctionValueTask
   Cel: Oblicz wartość funkcji dla danego argumentu.
   Zakres: absolutne podstawy pojęcia funkcji
============================================================= */

/* ---------- helpers ---------- */

function randInt(min, max, excludeZero = false) {
  let v;
  do {
    v = Math.floor(Math.random() * (max - min + 1)) + min;
  } while (excludeZero && v === 0);
  return v;
}

function sampleNonTrivialX() {
  // tylko: {-6..-2, 2..6}
  const mag = randInt(2, 6);
  return Math.random() < 0.5 ? -mag : mag;
}

function formatLinearLatex(a, b) {
  const aStr = a === 1 ? "" : a === -1 ? "-" : String(a);
  const bStr = b === 0 ? "" : b > 0 ? ` + ${b}` : ` - ${Math.abs(b)}`;
  return `${aStr}x${bStr}`;
}

function formatQuadraticLatex(a, b, c) {
  const aStr = a === 1 ? "" : a === -1 ? "-" : String(a);
  const bStr = b === 0 ? "" : b > 0 ? ` + ${b}x` : ` - ${Math.abs(b)}x`;
  const cStr = c === 0 ? "" : c > 0 ? ` + ${c}` : ` - ${Math.abs(c)}`;
  return `${aStr}x^2${bStr}${cStr}`;
}

/* ---------- GENERATOR ---------- */

export function generateTask() {
  const isQuadratic = Math.random() < 0.4; // głównie liniowe, ale czasem kwadrat

  let data;

  if (isQuadratic) {
    const a = randInt(-3, 3, true);
    const b = randInt(-5, 5);
    const c = randInt(-5, 5);
    const x = sampleNonTrivialX();

    data = {
      kind: "quadratic",
      a,
      b,
      c,
      x,
      value: a * x * x + b * x + c,
    };
  } else {
    const a = randInt(-5, 5, true);
    const b = randInt(-5, 5);
    const x = sampleNonTrivialX();

    data = {
      kind: "linear",
      a,
      b,
      x,
      value: a * x + b,
    };
  }

  return {
    type: "ComputeFunctionValueTask",
    data,
  };
}

/* ---------- RENDER TASK ---------- */

export function renderTask({ data }) {
  const { kind, a, b, c, x } = data;

  const latex =
    kind === "linear" ? formatLinearLatex(a, b) : formatQuadraticLatex(a, b, c);

  return (
    <div style={{ marginTop: 6 }}>
      <div>
        Dana jest funkcja:
        <BlockMath math={`f(x) = ${latex}`} />
      </div>
      <div>
        Oblicz wartość funkcji dla argumentu{" "}
        <InlineMath math={`x = ${x}`} />.
      </div>
    </div>
  );
}

/* ---------- INPUT ---------- */

export function renderInput(_task, userAnswer, onChange, disabled) {
  return (
    <div style={{ marginTop: 10 }}>
      <InlineMath math={`f(x) =`} />
      <FractionInput
        value={userAnswer || ""}
        onChange={onChange}
        disabled={disabled}
        label="f(x)"
      />
    </div>
  );
}

/* ---------- CHECK ---------- */

export function checkAnswer({ data }, userAnswer) {
  if (!userAnswer || typeof userAnswer !== "object") return false;
  const parsed = userAnswer.parsed;
  if (parsed == null || isNaN(parsed)) return false;

  return Math.abs(parsed - data.value) < 0.001;
}

/* ---------- ANSWER ---------- */

export function renderAnswer({ data }) {
  return <InlineMath math={`f(${data.x}) = ${data.value}`} />;
}

/* ---------- EXPLANATION ---------- */

export function renderExplanation({ data }) {
  const { kind, a, b, c, x, value } = data;

  if (kind === "linear") {
    return (
      <div style={{ textAlign: "left", lineHeight: 1.7 }}>
        <div>
          <strong>Krok 1.</strong> Zapis funkcji:
        </div>
        <BlockMath math={`f(x) = ${formatLinearLatex(a, b)}`} />

        <div>
          <strong>Krok 2.</strong> Podstawiamy x = {x}:
        </div>
        <BlockMath
          math={`f(${x}) = ${a}\\cdot ${x} ${b >= 0 ? "+ " + b : "- " + Math.abs(b)}`}
        />

        <div>
          <strong>Krok 3.</strong> Obliczamy:
        </div>
        <BlockMath math={`f(${x}) = ${value}`} />
      </div>
    );
  }

  return (
    <div style={{ textAlign: "left", lineHeight: 1.7 }}>
      <div>
        <strong>Krok 1.</strong> Zapis funkcji:
      </div>
      <BlockMath math={`f(x) = ${formatQuadraticLatex(a, b, c)}`} />

      <div>
        <strong>Krok 2.</strong> Podstawiamy x = {x}:
      </div>
      <BlockMath
        math={`f(${x}) = ${a}\\cdot ${x}^2 ${b >= 0 ? "+ " + b + "\\cdot " + x : "- " + Math.abs(b) + "\\cdot " + x} ${c >= 0 ? "+ " + c : "- " + Math.abs(c)}`}
      />

      <div>
        <strong>Krok 3.</strong> Obliczamy:
      </div>
      <BlockMath math={`f(${x}) = ${value}`} />
    </div>
  );
}

/* ---------- USER ANSWER ---------- */

export function renderUserAnswer(userAnswer) {
  if (!userAnswer || !userAnswer.raw) {
    return <i>(brak odpowiedzi)</i>;
  }

  return <InlineMath math={`f(x) = ${userAnswer.raw}`} />;
}

export default {
  generateTask,
  renderTask,
  renderInput,
  checkAnswer,
  renderAnswer,
  renderExplanation,
  renderUserAnswer,
};
